
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ContentAdaptLayout from '../../../../_components/content-adapt-layout/content-adapt-layout.vue'

import {
  GroupPolicySettingsService,
  GroupPolicyTypes,
  EarlyWarningAndMonitoringSettings,
  EarlyWarningFrequency
} from '../../group-policy-settings.service'

import {
  DEFAULT_AFTERNOON_RANGE,
  DEFAULT_MORNING_RANGE,
  DEFAULT_PARAMS,
  buildWarningRows,
  FREQUENCY_OPTIONS,
  promptSetting,
  setSliderData,
  WarningData
} from './early-warning-and-monitoring.service'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'

export default defineComponent({
  components: {
    ContentAdaptLayout
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    policyId: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: ''
    },
    activeName: {
      type: String,
      default: ''
    }
  },
  setup (props, context) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector()
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)

    const loading = ref<boolean>(true)

    const morningRangeNode = ref<HTMLElement>(null)
    const afternoonRangeNode = ref<HTMLElement>(null)

    const morningRange = ref<number[]>(DEFAULT_MORNING_RANGE)
    const afternoonRange = ref<number[]>(DEFAULT_AFTERNOON_RANGE)

    const strategy = reactive<EarlyWarningAndMonitoringSettings>({
      ...DEFAULT_PARAMS
    })

    const data = reactive({
      hasEdit: false
    })

    const frequencyOptions = FREQUENCY_OPTIONS

    const warningData = computed<WarningData[]>(() => {
      const _localeDep = locale.value
      return buildWarningRows(t)
    })
    function getSettings () {
      groupPolicySettingsService.getSettings({
        id: props.policyId,
        type: GroupPolicyTypes.EarlyWarningAndMonitoring
      }).then(response => {
        const settingsData = response.strategy as EarlyWarningAndMonitoringSettings

        setSliderData({
          data: settingsData,
          morningRange,
          afternoonRange,
          morningRangeNode,
          afternoonRangeNode
        })

        Object.assign(
          strategy,
          {
            ...DEFAULT_PARAMS
          },
          settingsData
        )
      }).finally(() => {
        loading.value = false
      })
    }

    function saveSettings () {
      groupPolicySettingsService.saveSettings({
        id: props.policyId,
        type: GroupPolicyTypes.EarlyWarningAndMonitoring,
        strategy: JSON.stringify(strategy)
      }).then(() => {
        groupPolicySettingsService.updateEditTab(false)
        context.emit('save')
      })
    }
    function setChangeStatus () {
      groupPolicySettingsService.updateEditTab(true)
    }
    function cancel () {
      context.emit('cancel')
    }

    groupPolicySettingsService.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })
    groupPolicySettingsService.saveTab.subscribe((name: string) => {
      if (name === 'early-warning-and-monitoring') {
        saveSettings()
      }
    })
    watch(() => props.activeName, (newV) => {
      if (newV === 'early-warning-and-monitoring') {
        getSettings()
      }
    })

    getSettings()

    return {
      t,
      OperateType,
      loading,
      morningRangeNode,
      afternoonRangeNode,
      strategy,
      frequencyOptions,
      warningData,
      morningRange,
      afternoonRange,
      setChangeStatus,
      data,
      EarlyWarningFrequency,
      marks: {
        0: '00:00',
        12: '12:00',
        24: '24:00'
      },
      cancel,
      formatTooltip (time: number) {
        if (time === null) {
          return
        }

        // eslint-disable-next-line no-magic-numbers
        return `${String(Math.floor((time))).padStart(2, '0')}:${String((time - Math.floor((time))) * 60).padStart(2, '0') || '00'}`
      },
      handlerMorningRangeChange (value: number[]) {
        setChangeStatus()
        // eslint-disable-next-line no-magic-numbers
        strategy.morning_start = value[0] * 60 * 60
        // eslint-disable-next-line no-magic-numbers
        strategy.morning_end = value[1] * 60 * 60
      },
      handlerAfternoonRangeChange (value: number[]) {
        setChangeStatus()
        // eslint-disable-next-line no-magic-numbers
        strategy.afternoon_start = value[0] * 60 * 60
        // eslint-disable-next-line no-magic-numbers
        strategy.afternoon_end = value[1] * 60 * 60
      },
      handlerEditSetting ($event: MouseEvent) {
        setChangeStatus()
        const {
          title,
          key,
          type
        } = ($event.currentTarget as HTMLElement).dataset
        promptSetting({
          data: warningData.value,
          strategy,
          title,
          key,
          type,
          t
        })
      },
      handlerSave () {
        saveSettings()
      }
    }
  }
})
